import { capitalize } from 'vue';

import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
import RunState from '@/common/enums/run-state.enum';
import { Creative, IABCategories } from '@/common/models';
import CreativeOptions from '@/common/models/creative/CreativeOptions';
import CreativeTypeOptions from '@/common/models/creative/CreativeTypeOptions';
import CreativeFormatOptions from '@/common/models/CreativeFormatOptions';
import CreativeOrientationOptions from '@/common/models/CreativeOrientationOptions';
import { convertRangesToDaypart, daypartToRanges, parseRanges } from '@/common/models/Dayparting';
import EntityProperty from '@/common/models/EntityProperty';
import {
  Filter,
  FilterCollection,
  FilterComparisonOperator,
  FilterLogicalOperator,
  QueryParams,
} from '@/common/models/query-params';
import { FullDaypart, getStore, InvalidDaypart } from '@/globals';
import {
  useAdvertisers,
  useLanguages,
  useMediaOwners,
  useMediaTypes,
  useNetworks,
} from '@/org-app/services/tanstack/queries';
import queryClient, { runQueryInScope, waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';
import { UPDATE_CREATIVE_IS_FALLBACK } from '@/org-app/store/modules/creatives/mutation-types';
import rootStore from '@/store';
import {
  UPDATE_CREATIVE_ADVERTISER_ID,
  UPDATE_CREATIVE_CREATIVEFORMATEXCLUSIONS,
  UPDATE_CREATIVE_CREATIVEFORMATS,
  UPDATE_CREATIVE_CREATIVEORIENTATIONEXCLUSIONS,
  UPDATE_CREATIVE_CREATIVEORIENTATIONS,
  UPDATE_CREATIVE_CREATIVETRACKINGURLS,
  UPDATE_CREATIVE_CREATIVETYPE,
  UPDATE_CREATIVE_DAYPART,
  UPDATE_CREATIVE_GEOFENCE_GROUP_EXCLUSION_IDS,
  UPDATE_CREATIVE_GEOFENCE_GROUP_IDS,
  UPDATE_CREATIVE_IAB_CATEGORY_IDS,
  UPDATE_CREATIVE_IS_MOBILE,
  UPDATE_CREATIVE_LANGUAGE_IDS,
  UPDATE_CREATIVE_LOCATIONIDS,
  UPDATE_CREATIVE_MARKET_IDS,
  UPDATE_CREATIVE_MARKETEXCLUSIONIDS,
  UPDATE_CREATIVE_MEDIATYPEEXCLUSIONIDS,
  UPDATE_CREATIVE_MEDIATYPEIDS,
  UPDATE_CREATIVE_NAME,
  UPDATE_CREATIVE_NETWORKEXCLUSIONIDS,
  UPDATE_CREATIVE_NETWORKIDS,
  UPDATE_CREATIVE_RUNSTATE,
  UPDATE_CREATIVE_SITEEXCLUSIONIDS,
  UPDATE_CREATIVE_SITEIDS,
  UPDATE_CREATIVE_UNITEXCLUSIONIDS,
  UPDATE_CREATIVE_UNITIDS,
  UPDATE_CREATIVE_UNITTAGEXCLUSIONS,
  UPDATE_CREATIVE_UNITTAGS,
} from '@/store/modules/models/mutation-types';
import rootState from '@/store/state';
import i18n from '@/vueI18n';

export const getEntityProperties = () => {
  const type = 'Creative';

  if (Creative.entityPropertiesCached != null) {
    return Creative.entityPropertiesCached;
  }

  const stateFilterSource = CreativeOptions.options().map((x) => ({ id: x.value, name: x.name, filters: x.filters }));
  const getExtraValidation = (creative) => creative.getExtraValidation();

  const runStateFilterSource = [
    {
      id: RunState.STOPPED,
      name: capitalize(i18n.global.t('actions.yes')),
    },
    {
      id: RunState.PLAYING,
      name: capitalize(i18n.global.t('actions.no')),
    },
  ];

  const action = async (creative) => {
    await getStore().dispatch('orgApp/updateCreative', { instance: creative });

    // When MO changes their own creatives, do not resend approvals to themselves
    // Only for all mediaOwners
    if (creative.isAllowedToSaveWithoutApprovals) {
      await getStore().dispatch('orgApp/saveCreativeWithoutApproval', creative);
    }
  };

  const getMediaOwners = async () => {
    const qry = useMediaOwners(queryClient);
    await waitForPendingQueries([qry]);
    return qry.items.value;
  };

  //
  // Logic to fetch markets, only one call will be performed.
  //
  const getMarkets = async () => {
    if (Creative.isGettingMarkets) {
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          if (!Creative.isGettingMarkets && Creative.marketsCache != null) {
            clearInterval(interval);
            resolve(Creative.marketsCache);
          }
        }, 500);
      });
    }
    Creative.isGettingMarkets = true;
    const mediaOwnerCountryRoots = await rootStore.dispatch('orgApp/getSupplierCountryRoots', { showLoader: false });
    const allMediaOwnerCountryIDs: number[][] = Object.values(mediaOwnerCountryRoots);
    const countryIDs: number[] = Array.from(new Set(allMediaOwnerCountryIDs.reduce((mem, x) => [...mem, ...x], [])));
    let ownerIDs: number[];
    const { isSupplyOrganization } = rootState.orgApp.organization;
    if (isSupplyOrganization) {
      ownerIDs = [rootState.orgApp.organization.id];
    } else {
      const mediaOwners = await getMediaOwners();
      ownerIDs = mediaOwners.map((mediaOwner) => mediaOwner.id);
    }
    Creative.marketsCache = await rootStore.dispatch('orgApp/getMarketsWithInventory', {
      ownerIDs,
      countryIDs,
      showLoader: false,
    });
    Creative.isGettingMarkets = false;
    return Creative.marketsCache;
  };

  //
  // Logic to fetch networks, only one call will be performed.
  //
  const getNetworks = async () => {
    const qry = useNetworks(queryClient);
    await waitForPendingQueries([qry]);
    return qry.items.value;
  };

  //
  // Logic to fetch mediaTypes, only one call will be performed.
  //
  const getMediaTypes = () => runQueryInScope(useMediaTypes);

  const getLocationEntityProperty = (labelKey: string, level: number, isExclusion = false) => {
    return new EntityProperty({
      label: capitalize(i18n.global.t(labelKey, { n: 2 })),
      property: isExclusion ? 'location_exclusion_ids' : 'location_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: isExclusion ? 'locationExclusionIDs' : 'locationIDs',
      mutation: UPDATE_CREATIVE_LOCATIONIDS,
      editableClassName: type,
      source: ({ queryParams = QueryParams.all(FilterCollection.for('level', [level])) }) => {
        return rootStore.dispatch('orgApp/getLocations', queryParams);
      },
      action,
      preMutationTransform: (id) => (id != null ? [Number.parseInt(id, 10)] : []),
      getExtraValidation,
      sourceQueryParamsForReadOnly: (entity: Creative) =>
        QueryParams.all(
          new FilterCollection(
            [
              new Filter('level', [level]),
              new Filter(
                'location_id',
                isExclusion ? entity.locationExclusionIDs : entity.locationIDs,
                FilterComparisonOperator.IN,
              ),
            ],
            FilterLogicalOperator.AND,
          ),
        ),
      ignoreUnmatchedValuesInSource: true,
    });
  };

  Creative.entityPropertiesCached = {
    id: new EntityProperty({
      label: '#',
      property: 'creative_id',
      type: TableColumnTypes.string,
      editablePropertyName: 'id',
    }),
    name: new EntityProperty({
      label: capitalize(i18n.global.t('names.name')),
      property: 'name',
      type: TableColumnTypes.string,
      editablePropertyName: 'name',
      mutation: UPDATE_CREATIVE_NAME,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    advertiser: new EntityProperty({
      label: capitalize(i18n.global.t('names.advertiser')),
      property: 'advertiser.name',
      type: TableColumnTypes.string,
      source: () => runQueryInScope(useAdvertisers),
      sourceFilterProperty: 'advertiser_id',
      editablePropertyName: 'advertiserID',
      mutation: UPDATE_CREATIVE_ADVERTISER_ID,
      editableClassName: type,
      postMutationCallback: (value, entity) => {
        entity.advertiser = value;
      },
      getExtraValidation,
      isInlineEditingAvailable: false,
    }),
    status: new EntityProperty({
      label: capitalize(i18n.global.t('names.status')),
      property: 'state',
      type: TableColumnTypes.string,
      source: stateFilterSource,
    }),
    runState: new EntityProperty({
      label: `${capitalize(i18n.global.t('states.paused'))}?`,
      property: 'run_state',
      type: TableColumnTypes.boolean,
      sourceFilterProperty: 'run_state',
      editablePropertyName: 'runState',
      mutation: UPDATE_CREATIVE_RUNSTATE,
      editableClassName: type,
      preMutationTransform: (val) => (val ? RunState.STOPPED : RunState.PLAYING),
      preActionTransform: (val) => (val ? RunState.STOPPED : RunState.PLAYING),
      action,
      getExtraValidation,
    }),
    iabCategories: new EntityProperty({
      label: capitalize(i18n.global.t('names.iabCategory', { n: 2 })),
      property: 'iabCategoriesFormatted',
      type: TableColumnTypes.string,
      source: IABCategories.hierarchicalOptions(),
      sourceFilterProperty: 'iab_categories',
      sourceGroupValues: 'subCategories',
      sourceGroupLabel: 'name',
      areSourceGroupParentsSelectable: true,
      editablePropertyName: 'iabCategories',
      mutation: UPDATE_CREATIVE_IAB_CATEGORY_IDS,
      editableClassName: type,
      editablePropertyAcceptsMultipleValues: true,
      preMutationTransform: (ids) => ids.map((id) => IABCategories.options().find((o) => o.id === id)),
      action,
      getExtraValidation,
    }),
    submittedOn: new EntityProperty({
      label: capitalize(i18n.global.t('messages.submittedOn')),
      property: 'submitted_at',
      type: TableColumnTypes.date,
    }),
    languages: new EntityProperty({
      label: capitalize(i18n.global.t('messages.languages')),
      property: 'languagesDescription',
      validationPropertyName: 'languages',
      type: TableColumnTypes.string,
      editablePropertyName: 'languageIDs',
      mutation: UPDATE_CREATIVE_LANGUAGE_IDS,
      editableClassName: type,
      source: () => runQueryInScope(useLanguages),
      sourceFilterProperty: 'language_ids',
      editablePropertyAcceptsMultipleValues: true,
      action,
      getExtraValidation,
    }),
    isFallback: new EntityProperty({
      label: capitalize(i18n.global.t('names.fallbackCreative')),
      property: 'is_fallback',
      type: TableColumnTypes.boolean,
      editablePropertyName: 'isFallback',
      mutation: UPDATE_CREATIVE_IS_FALLBACK,
      editableClassName: type,
      action,
      getExtraValidation,
      defaultValue: { label: capitalize(i18n.global.t('actions.no')), value: false },
    }),
    isMobile: new EntityProperty({
      label: capitalize(i18n.global.t('names.mobileCreative')),
      property: 'is_mobile',
      type: TableColumnTypes.boolean,
      editablePropertyName: 'isMobile',
      mutation: UPDATE_CREATIVE_IS_MOBILE,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    creativeType: new EntityProperty({
      label: capitalize(i18n.global.t('names.creativeType')),
      property: 'creative_type',
      type: TableColumnTypes.string,
      editablePropertyName: 'creativeType',
      mutation: UPDATE_CREATIVE_CREATIVETYPE,
      editableClassName: type,
      source: CreativeTypeOptions.options().map((x) => ({
        id: x.value,
        name: x.name,
      })),
      isInlineEditingAvailable: false,
    }),
    trackingUrls: new EntityProperty({
      label: capitalize(i18n.global.t('names.creativeTrackingUrl', { n: 2 })),
      property: 'tracking',
      type: TableColumnTypes.list,
      editablePropertyName: 'trackingUrls',
      mutation: UPDATE_CREATIVE_CREATIVETRACKINGURLS,
      editableClassName: type,
      isInlineEditingAvailable: false,
    }),
    markets: new EntityProperty({
      label: capitalize(i18n.global.t('names.market', { n: 2 })),
      property: 'market_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getMarkets,
      sourceFilterProperty: 'market_id',
      editablePropertyName: 'marketIDs',
      mutation: UPDATE_CREATIVE_MARKET_IDS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    marketExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.marketExclusion', { n: 2 })),
      property: 'market_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getMarkets,
      sourceFilterProperty: 'market_id',
      editablePropertyName: 'marketExclusionIDs',
      mutation: UPDATE_CREATIVE_MARKETEXCLUSIONIDS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    networks: new EntityProperty({
      label: capitalize(i18n.global.t('names.network', { n: 2 })),
      property: 'network_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getNetworks,
      sourceFilterProperty: 'network_id',
      editablePropertyName: 'networkIDs',
      mutation: UPDATE_CREATIVE_NETWORKIDS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    networkExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.networkExclusion', { n: 2 })),
      property: 'network_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getNetworks,
      sourceFilterProperty: 'network_id',
      editablePropertyName: 'networkExclusionIDs',
      mutation: UPDATE_CREATIVE_NETWORKEXCLUSIONIDS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    mediaTypes: new EntityProperty({
      label: capitalize(i18n.global.t('names.venueType', { n: 2 })),
      property: 'mediatype_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getMediaTypes,
      sourceFilterProperty: 'mediatype_id',
      sourceGroupValues: 'children',
      sourceGroupLabel: 'name',
      editablePropertyName: 'mediaTypeIDs',
      mutation: UPDATE_CREATIVE_MEDIATYPEIDS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    mediaTypeExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.venueTypeExclusion', { n: 2 })),
      property: 'mediatype_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getMediaTypes,
      sourceFilterProperty: 'mediatype_id',
      sourceGroupValues: 'children',
      sourceGroupLabel: 'name',
      editablePropertyName: 'mediaTypeExclusionIDs',
      mutation: UPDATE_CREATIVE_MEDIATYPEEXCLUSIONIDS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    preciseDaypart: new EntityProperty({
      label: capitalize(i18n.global.t('names.daypart', { n: 2 })),
      property: 'precise_daypart',
      type: TableColumnTypes.string,
      editablePropertyName: 'preciseDaypartRanges',
      validationPropertyName: 'preciseDaypart',
      mutation: UPDATE_CREATIVE_DAYPART,
      editableClassName: type,
      preMutationTransform: (text) => {
        if (text == null || text === '') return FullDaypart;
        try {
          const result = parseRanges(text);
          return convertRangesToDaypart(result.ranges);
        } catch (e) {
          return InvalidDaypart;
        }
      },
      action,
      getExtraValidation,
      canAffectAdDeliveryWhenModified: true,
      bulkInputNote: 'Note that hours are inclusive.',
      bulkInputExamples: ['Mon-Sun 6-17', 'Mon 12-16 Tue-Fri 8-9'],
      defaultValue: {
        label: daypartToRanges(FullDaypart),
        value: FullDaypart,
      },
    }),
    creativeOrientations: new EntityProperty({
      label: capitalize(i18n.global.t('names.creativeOrientation', { n: 2 })),
      property: 'creative_orientations',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'creativeOrientations',
      mutation: UPDATE_CREATIVE_CREATIVEORIENTATIONS,
      editableClassName: type,
      source: CreativeOrientationOptions.options(),
      action,
      getExtraValidation,
    }),
    creativeOrientationExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.creativeOrientationExclusion', { n: 2 })),
      property: 'creative_orientation_exclusions',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'creativeOrientationExclusions',
      mutation: UPDATE_CREATIVE_CREATIVEORIENTATIONEXCLUSIONS,
      editableClassName: type,
      source: CreativeOrientationOptions.options(),
      action,
      getExtraValidation,
    }),
    creativeFormats: new EntityProperty({
      label: capitalize(i18n.global.t('names.creativeFormat', { n: 2 })),
      property: 'creative_formats',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'creativeFormats',
      mutation: UPDATE_CREATIVE_CREATIVEFORMATS,
      editableClassName: type,
      source: CreativeFormatOptions.options(),
      action,
      getExtraValidation,
    }),
    creativeFormatExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.creativeFormatExclusion', { n: 2 })),
      property: 'creative_format_exclusions',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'creativeFormatExclusions',
      mutation: UPDATE_CREATIVE_CREATIVEFORMATEXCLUSIONS,
      editableClassName: type,
      source: CreativeFormatOptions.options(),
      action,
      getExtraValidation,
    }),
    unitTags: new EntityProperty({
      label: capitalize(i18n.global.t('names.tag', { n: 2 })),
      property: 'unit_tags',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'unitTags',
      mutation: UPDATE_CREATIVE_UNITTAGS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    unitTagExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.tagExclusion', { n: 2 })),
      property: 'unit_tag_exclusions',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'unitTagExclusions',
      mutation: UPDATE_CREATIVE_UNITTAGEXCLUSIONS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    countries: getLocationEntityProperty('names.country', 0),
    regions: getLocationEntityProperty('names.region', 1),
    cities: getLocationEntityProperty('names.city', 3),
    countryExclusions: getLocationEntityProperty('names.countryExclusion', 0, true),
    regionExclusions: getLocationEntityProperty('names.regionExclusion', 1, true),
    cityExclusions: getLocationEntityProperty('names.cityExclusion', 3, true),
    sites: new EntityProperty({
      label: capitalize(i18n.global.t('names.site', { n: 2 })),
      property: 'site_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'siteIDs',
      mutation: UPDATE_CREATIVE_SITEIDS,
      editableClassName: type,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getSites', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: Creative) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('site_id', entity.siteIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
        ),
      action,
      getExtraValidation,
    }),
    siteExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.siteExclusion', { n: 2 })),
      property: 'site_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'siteExclusionIDs',
      mutation: UPDATE_CREATIVE_SITEEXCLUSIONIDS,
      editableClassName: type,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getSites', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: Creative) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('site_id', entity.siteExclusionIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
        ),
      action,
      getExtraValidation,
    }),
    screens: new EntityProperty({
      label: capitalize(i18n.global.t('names.screen', { n: 2 })),
      property: 'unit_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'unitIDs',
      mutation: UPDATE_CREATIVE_UNITIDS,
      editableClassName: type,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getUnits', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: Creative) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('unit_id', entity.unitIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
          ['unit_id', 'external_id', 'name'],
        ),
      action,
      getExtraValidation,
    }),
    screenExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.screenExclusion', { n: 2 })),
      property: 'unit_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'unitExclusionIDs',
      mutation: UPDATE_CREATIVE_UNITEXCLUSIONIDS,
      editableClassName: type,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getUnits', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: Creative) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('unit_id', entity.unitExclusionIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
          ['unit_id', 'external_id', 'name'],
        ),
      action,
      getExtraValidation,
    }),
    proximityGeofenceGroups: new EntityProperty({
      label: capitalize(i18n.global.t('names.proximityGeofenceGroup', { n: 2 })),
      property: 'geofencegroup_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'geofenceGroupIDs',
      mutation: UPDATE_CREATIVE_GEOFENCE_GROUP_IDS,
      editableClassName: type,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getGeofenceGroups', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: Creative) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('geofencegroup_id', entity.geofenceGroupIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
        ),
      action,
      getExtraValidation,
    }),
    proximityGeofenceGroupExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.proximityGeofenceGroupExclusion', { n: 2 })),
      property: 'geofencegroup_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'geofenceGroupExclusionIDs',
      mutation: UPDATE_CREATIVE_GEOFENCE_GROUP_EXCLUSION_IDS,
      editableClassName: type,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getGeofenceGroups', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: Creative) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('geofencegroup_id', entity.geofenceGroupExclusionIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
        ),
      action,
      getExtraValidation,
    }),
    targetCriteriaWeather: new EntityProperty({
      label: capitalize(i18n.global.t('names.weatherCondition', { n: 2 })),
      property: 'targetCriteriaWeather',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaTemperature: new EntityProperty({
      label: capitalize(i18n.global.t('names.temperature', { n: 1 })),
      property: 'targetCriteriaTemperature',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaUV: new EntityProperty({
      label: capitalize(i18n.global.t('messages.uvTargeting', { n: 1 })),
      property: 'targetCriteriaUV',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaPollen: new EntityProperty({
      label: capitalize(i18n.global.t('messages.pollenTargeting', { n: 1 })),
      property: 'targetCriteriaPollen',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaDestinationWeather: new EntityProperty({
      label: capitalize(i18n.global.t('messages.destinationTargetWeatherConditions')),
      property: 'targetCriteriaDestinationWeather',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaDestinationTemperature: new EntityProperty({
      label: capitalize(i18n.global.t('messages.destinationTargetTemperature')),
      property: 'targetCriteriaDestinationTemperature',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaDestinationRain: new EntityProperty({
      label: capitalize(i18n.global.t('messages.destinationTargetRainAccumulation')),
      property: 'targetCriteriaDestinationRain',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaDestinationSnow: new EntityProperty({
      label: capitalize(i18n.global.t('messages.destinationTargetSnowAccumulation')),
      property: 'targetCriteriaDestinationSnow',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaCustomEvents: new EntityProperty({
      label: capitalize(i18n.global.t('names.customEvent', { n: 2 })),
      property: 'targetCriteriaCustomEvents',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    creativeProperties: new EntityProperty({
      label: capitalize(i18n.global.t('names.property', { n: 2 })),
      property: 'creativeProperties',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
  };

  return Creative.entityPropertiesCached;
};
